import {
  ActionManager,
  Color4,
  CreateBoxVertexData,
  ExecuteCodeAction,
  Mesh,
  Nullable,
  Vector2,
  Vector3,
  VertexBuffer,
  PointerDragBehavior,
  Matrix,
} from '@babylonjs/core'
import { useCallback, useEffect } from 'react'
import {
  createWallGaps,
  cutOpeningHolesInWallMesh,
  isDoorMesh,
  isSketchMesh,
  isWallMesh,
  isWindowMesh,
} from '../utils'
import { SelectionManager } from '../selection/selectionManager'
import { equalsWithEpsilon } from '../../utils/Common'
import { useDispatch } from 'react-redux'
import store, { AppDispatch } from '../../store'
import { editWall } from '../../actions/construction'
import { updateHistory } from '../../actions/history'
import { ActionType } from '../../reducers/history'
import { SeparateElementUI } from './useSeparateWallTool'
import { SceneManager } from '../sceneManager'
import {
  isAntiParallel,
  isOrthoLinear,
  rayBoundingBoxIntersection,
} from '../../utils/MathUtil'

export enum WallProperty {
  Length = 'Length',
  Thickness = 'Thickness',
  CenterPoint = 'CenterPoint',
}

export enum WallAnchor {
  Start = 'Start',
  Center = 'Center',
  End = 'End',
}

type SaveResetOptions = WallProperty | WallProperty[]

const calculateWallProps = (wallMesh: Mesh) => {
  const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
  const { x: wallHalfLength, z: wallHalfThickness } = wallBoundingBox.extendSize

  return {
    length: wallHalfLength * 2,
    thickness: wallHalfThickness * 2,
    centerPoint: {
      x: wallMesh.position.x,
      z: wallMesh.position.z,
    },
  }
}

interface EditWallUtils {
  readonly changeWallPosition: (value: Vector2) => void
  readonly changeWallLength: (value: number, anchor?: WallAnchor) => void
  readonly changeWallThickness: (value: number, anchor?: WallAnchor) => void
  readonly saveChanges: (options?: SaveResetOptions) => void
  readonly resetChanges: (options?: SaveResetOptions) => void
}

type WallProperties = {
  [WallProperty.CenterPoint]: Vector2
  [WallProperty.Length]: {
    value: number
    anchor: WallAnchor
  }
  [WallProperty.Thickness]: {
    value: number
    anchor: WallAnchor
  }
}

const wallPropsMap = new Map<string, { count: number; props: WallProperties }>()

/**
 * Custom hook to perform wall editing and saving or dismissing changes
 * @param wallMesh mesh of Wall type to edit (Other mesh types will be ignored)
 * @param addAction if true, will add OnKeyDownTrigger action to perform editing using pointer (Default: true)
 * @returns readonly object that contains functions to edit, save, and reset changes
 */
export const useEditWallTool = (
  wallMesh: Mesh,
  addAction: boolean = true,
): EditWallUtils => {
  const dispatch = useDispatch<AppDispatch>()

  const changeWallPosition = useCallback(
    ({ x, y }: Vector2) => {
      if (!isWallMesh(wallMesh)) {
        console.warn(`useEditWallTool supports only meshes of 'Wall' type`, {
          mesh: wallMesh,
        })

        return
      }

      wallMesh.position.x = x
      wallMesh.position.z = y

      // Trigger Measurements UI position change
      wallMesh.computeWorldMatrix(true)
    },
    [wallMesh],
  )

  const changeWallLength = useCallback(
    (value: number, anchor: WallAnchor = WallAnchor.Center) => {
      if (!isWallMesh(wallMesh)) {
        console.warn(`useEditWallTool supports only meshes of 'Wall' type`, {
          mesh: wallMesh,
        })

        return
      }

      const wallProps = wallPropsMap.get(wallMesh.id)?.props
      if (!wallProps) {
        return
      }

      const newLength = Math.max(value, 1e-3)
      const boundingBox = wallMesh.getBoundingInfo().boundingBox
      const {
        x: halfLength,
        y: halfHeight,
        z: halfThickness,
      } = boundingBox.extendSize

      // No need to resize wall if current length is equal to passed value
      if (equalsWithEpsilon(newLength, halfLength * 2)) {
        return
      }

      const { sketchMeshes, openingMeshes } = wallMesh
        .getChildMeshes(true, (node): node is Mesh => node instanceof Mesh)
        .reduce<{ sketchMeshes: Mesh[]; openingMeshes: Mesh[] }>(
          (acc, current) => {
            if (isSketchMesh(current)) {
              acc.sketchMeshes.push(current)
            }

            if (isWindowMesh(current) || isDoorMesh(current)) {
              current.setParent(null)
              acc.openingMeshes.push(current)
            }

            return acc
          },
          { sketchMeshes: [], openingMeshes: [] },
        )

      const vertexData = CreateBoxVertexData({
        width: newLength,
        height: halfHeight * 2,
        depth: halfThickness * 2,
        faceColors: [
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(0, 0, 0, 255),
          Color4.FromInts(255, 255, 255, 255),
        ],
      })

      vertexData.applyToMesh(wallMesh)

      if (anchor === WallAnchor.Start || anchor === WallAnchor.End) {
        const diff = newLength - halfLength * 2
        const direction = wallMesh.metadata.wallDirection as Vector3
        direction.scaleAndAddToRef(
          anchor === WallAnchor.Start ? diff / 2 : -diff / 2,
          wallMesh.position,
        )
      }

      // Trigger Measurements UI position change
      wallMesh.computeWorldMatrix(true)

      const selectionManager = SelectionManager.getInstance()
      if (selectionManager.isSelected(wallMesh.id)) {
        // Update selection edges
        wallMesh.enableEdgesRendering(0.9999)
      }

      // Update Sketch mesh of wall
      if (vertexData.positions) {
        const data = new Float32Array(vertexData.positions)
        sketchMeshes.forEach((sketchMesh) => {
          sketchMesh.setVerticesData(VertexBuffer.PositionKind, data)
        })
      }

      cutOpeningHolesInWallMesh(wallMesh, openingMeshes)
      openingMeshes.forEach((mesh) => mesh.setParent(wallMesh))

      wallProps[WallProperty.Length].anchor = anchor
    },
    [wallMesh],
  )

  const changeWallThickness = useCallback(
    (value: number, anchor: WallAnchor = WallAnchor.Center) => {
      if (!isWallMesh(wallMesh)) {
        console.warn(`useEditWallTool supports only meshes of 'Wall' type`, {
          mesh: wallMesh,
        })

        return
      }

      const wallProps = wallPropsMap.get(wallMesh.id)?.props
      if (!wallProps) {
        return
      }

      const newThickness = Math.max(value, 1e-3)
      const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
      const {
        x: wallHalfLength,
        y: wallHalfHeight,
        z: wallHalfThickness,
      } = wallBoundingBox.extendSize

      // No need to resize wall if current thickness is equal to passed value
      if (equalsWithEpsilon(newThickness, wallHalfThickness * 2)) {
        return
      }

      const { sketchMeshes, openingMeshes } = wallMesh
        .getChildMeshes(true, (node): node is Mesh => node instanceof Mesh)
        .reduce<{ sketchMeshes: Mesh[]; openingMeshes: Mesh[] }>(
          (acc, current) => {
            if (isSketchMesh(current)) {
              acc.sketchMeshes.push(current)
            }

            if (isWindowMesh(current) || isDoorMesh(current)) {
              acc.openingMeshes.push(current)
            }

            return acc
          },
          { sketchMeshes: [], openingMeshes: [] },
        )

      const vertexData = CreateBoxVertexData({
        width: wallHalfLength * 2,
        height: wallHalfHeight * 2,
        depth: newThickness,
        faceColors: [
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(0, 0, 0, 255),
          Color4.FromInts(255, 255, 255, 255),
        ],
      })

      vertexData.applyToMesh(wallMesh)

      if (anchor === WallAnchor.Start || anchor === WallAnchor.End) {
        const diff = newThickness - wallHalfThickness * 2
        const direction = wallMesh.metadata.wallNormal as Vector3
        direction.scaleAndAddToRef(
          anchor === WallAnchor.Start ? diff / 2 : -diff / 2,
          wallMesh.position,
        )
      }

      // Trigger Measurements UI position change
      wallMesh.computeWorldMatrix(true)

      const selectionManager = SelectionManager.getInstance()
      if (selectionManager.isSelected(wallMesh.id)) {
        // Update selection edges
        wallMesh.enableEdgesRendering(0.9999)
      }

      // Update Sketch mesh of wall
      if (vertexData.positions) {
        const data = new Float32Array(vertexData.positions)
        sketchMeshes.forEach((sketchMesh) => {
          sketchMesh.setVerticesData(VertexBuffer.PositionKind, data)
        })
      }

      openingMeshes.forEach((openingMesh) => {
        const openingBoundingBox = openingMesh.getBoundingInfo().boundingBox
        const { x: halfWidth, y: halfHeight } = openingBoundingBox.extendSize

        const vertexData = CreateBoxVertexData({
          width: halfWidth * 2,
          height: halfHeight * 2,
          depth: newThickness,
          faceColors: [
            Color4.FromInts(255, 255, 255, 255),
            Color4.FromInts(255, 255, 255, 255),
            Color4.FromInts(255, 255, 255, 255),
            Color4.FromInts(255, 255, 255, 255),
            Color4.FromInts(0, 0, 0, 255),
            Color4.FromInts(255, 255, 255, 255),
          ],
        })

        vertexData.applyToMesh(openingMesh)

        const sketchMeshes = openingMesh
          .getChildMeshes(true, (node): node is Mesh => node instanceof Mesh)
          .reduce<Mesh[]>((acc, current) => {
            if (isSketchMesh(current)) {
              acc.push(current)
            }

            return acc
          }, [])

        // Update Sketch mesh of opening
        if (vertexData.positions) {
          const data = new Float32Array(vertexData.positions)
          sketchMeshes.forEach((sketchMesh) => {
            sketchMesh.setVerticesData(VertexBuffer.PositionKind, data)
          })
        }
      })

      cutOpeningHolesInWallMesh(wallMesh, openingMeshes)

      wallProps[WallProperty.Thickness].anchor = anchor
    },
    [wallMesh],
  )

  const saveChanges = useCallback(
    (option?: SaveResetOptions) => {
      if (!isWallMesh(wallMesh)) {
        console.warn(`useEditWallTool supports only meshes of 'Wall' type`, {
          mesh: wallMesh,
        })

        return
      }

      const wallProps = wallPropsMap.get(wallMesh.id)?.props
      if (!wallProps) {
        return
      }

      const savedData = {
        length: wallProps[WallProperty.Length].value,
        thickness: wallProps[WallProperty.Thickness].value,
        centerPoint: {
          x: wallProps[WallProperty.CenterPoint].x,
          z: wallProps[WallProperty.CenterPoint].y,
        },
      }
      const options = option ? (Array.isArray(option) ? option : [option]) : []

      const { length, thickness, centerPoint } = calculateWallProps(wallMesh)
      if (!options.length || options.includes(WallProperty.Length)) {
        savedData.length = length
      }

      if (!options.length || options.includes(WallProperty.Thickness)) {
        savedData.thickness = thickness
      }

      if (!options.length || options.includes(WallProperty.CenterPoint)) {
        savedData.centerPoint.x = centerPoint.x
        savedData.centerPoint.z = centerPoint.z
      }

      if (
        !equalsWithEpsilon(
          wallProps[WallProperty.Length].value,
          savedData.length,
        ) ||
        !equalsWithEpsilon(
          wallProps[WallProperty.Thickness].value,
          savedData.thickness,
        ) ||
        !equalsWithEpsilon(
          wallProps[WallProperty.CenterPoint].x,
          savedData.centerPoint.x,
        ) ||
        !equalsWithEpsilon(
          wallProps[WallProperty.CenterPoint].y,
          savedData.centerPoint.z,
        )
      ) {
        const { length, thickness, centerPoint } = savedData
        dispatch(
          editWall({
            id: wallMesh.id,
            length,
            thickness,
            center_point: [centerPoint.x, centerPoint.z],
            anchor: wallProps[WallProperty.Length].anchor,
          }),
        )

        const convertData = store.getState().construction.convertData
        dispatch(
          updateHistory({
            convertData,
            items: [
              {
                itemId: wallMesh.id,
                actionType: ActionType.EDIT,
              },
            ],
          }),
        )

        wallProps[WallProperty.Length].value = length
        wallProps[WallProperty.Thickness].value = thickness
        wallProps[WallProperty.CenterPoint].set(centerPoint.x, centerPoint.z)

        createWallGaps()
      }
    },
    [wallMesh, dispatch],
  )

  const resetChanges = useCallback(
    (option?: SaveResetOptions) => {
      if (!isWallMesh(wallMesh)) {
        console.warn(`useEditWallTool supports only meshes of 'Wall' type`, {
          mesh: wallMesh,
        })

        return
      }

      const wallProps = wallPropsMap.get(wallMesh.id)?.props
      if (!wallProps) {
        return
      }

      const options = option ? (Array.isArray(option) ? option : [option]) : []
      if (!options.length || options.includes(WallProperty.Length)) {
        const { value, anchor } = wallProps[WallProperty.Length]
        changeWallLength(value, anchor)
      }

      if (!options.length || options.includes(WallProperty.Thickness)) {
        const { value, anchor } = wallProps[WallProperty.Thickness]
        changeWallThickness(value, anchor)
      }

      if (!options.length || options.includes(WallProperty.CenterPoint)) {
        changeWallPosition(wallProps[WallProperty.CenterPoint])
      }
    },
    [wallMesh, changeWallLength, changeWallThickness, changeWallPosition],
  )

  useEffect(() => {
    if (!isWallMesh(wallMesh)) {
      console.warn(`useEditWallTool supports only meshes of 'Wall' type`, {
        mesh: wallMesh,
      })

      return
    }

    if (!wallPropsMap.has(wallMesh.id)) {
      const { length, thickness, centerPoint } = calculateWallProps(wallMesh)
      wallPropsMap.set(wallMesh.id, {
        count: 1,
        props: {
          [WallProperty.Length]: {
            value: length,
            anchor: WallAnchor.Center,
          },
          [WallProperty.Thickness]: {
            value: thickness,
            anchor: WallAnchor.Center,
          },
          [WallProperty.CenterPoint]: new Vector2(centerPoint.x, centerPoint.z),
        },
      })
    } else {
      const obj = wallPropsMap.get(wallMesh.id)
      if (obj) {
        obj.count += 1
      }
    }

    let action: Nullable<ExecuteCodeAction> = null
    if (addAction) {
      const DRAG_ACTIVATION_DISTANCE = 2.5
      const localMatrix = new Matrix()
      const localPoint = new Vector3()
      action = new ExecuteCodeAction(
        ActionManager.OnPickDownTrigger,
        ({ sourceEvent }) => {
          const isCtrlPressed = sourceEvent.ctrlKey
          const scene = SceneManager.getInstance().getScene()
          const isSeparateWallUIVisible = SeparateElementUI.isUIVisible
          if (isCtrlPressed || !scene || isSeparateWallUIVisible) {
            return
          }

          const transform = wallMesh.getWorldMatrix()
          const wallDirection = Vector3.TransformNormalFromFloatsToRef(
            1,
            0,
            0,
            transform,
            new Vector3(),
          )
          const wallNormal = Vector3.TransformNormalFromFloatsToRef(
            0,
            0,
            1,
            transform,
            new Vector3(),
          )

          const ray = scene.createPickingRay(
            scene.pointerX,
            scene.pointerY,
            null,
            scene.activeCamera,
          )
          const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
          const { pickNormal, pickPoint } =
            rayBoundingBoxIntersection(ray, wallBoundingBox) ?? {}
          if (!pickNormal || !pickPoint) {
            return
          }

          let anchor: Nullable<WallAnchor> = null
          // Wall Length Shrinking / Stretching
          if (isOrthoLinear(pickNormal, wallDirection)) {
            const isFromBeginning = isAntiParallel(pickNormal, wallDirection)
            anchor = isFromBeginning ? WallAnchor.End : WallAnchor.Start
          } else if (isOrthoLinear(pickNormal, wallNormal)) {
            const edgePoint = wallBoundingBox.extendSize.x * 0.75
            wallMesh.getWorldMatrix().invertToRef(localMatrix)
            Vector3.TransformCoordinatesToRef(
              pickPoint,
              localMatrix,
              localPoint,
            )

            const isInTheMiddle = Math.abs(localPoint.x) <= edgePoint
            if (!isInTheMiddle) {
              pickNormal.set(localPoint.x > 0 ? 1 : -1, 0, 0)
              Vector3.TransformNormalToRef(
                pickNormal,
                wallMesh.getWorldMatrix(),
                pickNormal,
              )

              const isFromBeginning = isAntiParallel(pickNormal, wallDirection)
              anchor = isFromBeginning ? WallAnchor.End : WallAnchor.Start
            }
          } else {
            return
          }

          const pointerDragBehavior = new PointerDragBehavior(
            anchor ? { dragAxis: wallDirection } : {},
          )
          pointerDragBehavior.useObjectOrientationForDragging = false
          pointerDragBehavior.moveAttached = false

          let isDragDirectionLocked = false
          const startPoint = new Vector3()
          const projectedWallDirection = new Vector3()
          const projectedWallNormal = new Vector3()
          const dragDirection = new Vector3()

          if (!anchor) {
            pointerDragBehavior.onDragStartObservable.add(
              ({ dragPlanePoint }) => {
                startPoint.copyFrom(dragPlanePoint)
                isDragDirectionLocked = false
              },
            )
          }

          let totalDragDistance = 0
          const { x, z } = wallMesh.position
          const gridSettings = store.getState().construction.gridSettings
          const cellSize = gridSettings.snapToGrid ? gridSettings.cellSize : 1
          pointerDragBehavior.onDragObservable.add(
            ({ dragDistance, dragPlaneNormal, dragPlanePoint }) => {
              totalDragDistance += dragDistance
              // Resize Action
              if (anchor) {
                const oldLength = wallBoundingBox.extendSize.x * 2
                let newLength = Math.max(
                  anchor === WallAnchor.Start
                    ? oldLength + totalDragDistance
                    : oldLength - totalDragDistance,
                  0,
                )

                if (gridSettings.snapToGrid) {
                  // TODO: Make wall end snap to grid
                  newLength = Math.round(newLength / cellSize) * cellSize
                }

                changeWallLength(newLength, anchor)
              }
              // Move Action
              else {
                if (isDragDirectionLocked) {
                  const newPosition = new Vector2(
                    x + dragDirection.x * totalDragDistance,
                    z + dragDirection.z * totalDragDistance,
                  )

                  if (gridSettings.snapToGrid) {
                    newPosition.x =
                      Math.round(newPosition.x / cellSize) * cellSize
                    newPosition.y =
                      Math.round(newPosition.y / cellSize) * cellSize
                  }

                  changeWallPosition(newPosition)
                } else {
                  const dragPlaneNormalLength = dragPlaneNormal.length()
                  const wallDirectionDot = Vector3.Dot(
                    wallDirection,
                    dragPlaneNormal,
                  )
                  projectedWallDirection.copyFrom(wallDirection)
                  dragPlaneNormal.scaleAndAddToRef(
                    -wallDirectionDot / dragPlaneNormalLength,
                    projectedWallDirection,
                  )

                  const wallNormalDot = Vector3.Dot(wallNormal, dragPlaneNormal)
                  projectedWallNormal.copyFrom(wallNormal)
                  dragPlaneNormal.scaleAndAddToRef(
                    -wallNormalDot / dragPlaneNormalLength,
                    projectedWallNormal,
                  )

                  dragPlanePoint.subtractToRef(startPoint, dragDirection)

                  const distanceX = Math.abs(
                    Vector3.Dot(projectedWallDirection, dragDirection),
                  )
                  const distanceZ = Math.abs(
                    Vector3.Dot(projectedWallNormal, dragDirection),
                  )
                  if (
                    dragDirection.length() > 0 &&
                    Math.max(distanceX, distanceZ) > DRAG_ACTIVATION_DISTANCE
                  ) {
                    isDragDirectionLocked = true
                    if (distanceX > distanceZ) {
                      dragDirection.copyFrom(wallDirection)
                    } else {
                      dragDirection.copyFrom(wallNormal)
                    }

                    pointerDragBehavior.options = {
                      dragAxis: dragDirection,
                    }
                  }
                }
              }
            },
          )

          pointerDragBehavior.onDragEndObservable.add(() => {
            wallMesh.removeBehavior(pointerDragBehavior)

            saveChanges()
          })

          wallMesh.addBehavior(pointerDragBehavior)
        },
      )

      wallMesh.actionManager?.registerAction(action)
    }

    return () => {
      if (action) {
        wallMesh.actionManager?.unregisterAction(action)
      }

      const obj = wallPropsMap.get(wallMesh.id)
      if (obj) {
        obj.count -= 1
        if (!obj.count) {
          const { length, thickness, centerPoint } =
            calculateWallProps(wallMesh)
          if (
            !equalsWithEpsilon(obj.props[WallProperty.Length].value, length) ||
            !equalsWithEpsilon(
              obj.props[WallProperty.Thickness].value,
              thickness,
            ) ||
            !equalsWithEpsilon(
              obj.props[WallProperty.CenterPoint].x,
              centerPoint.x,
            ) ||
            !equalsWithEpsilon(
              obj.props[WallProperty.CenterPoint].y,
              centerPoint.z,
            )
          ) {
            resetChanges()
          }
          wallPropsMap.delete(wallMesh.id)
        }
      }
    }
  }, [
    wallMesh,
    addAction,
    changeWallPosition,
    changeWallLength,
    saveChanges,
    resetChanges,
  ])

  return {
    changeWallPosition,
    changeWallLength,
    changeWallThickness,
    saveChanges,
    resetChanges,
  }
}
